import { useSceneActivity } from "./ui";
import { motion } from "framer-motion";
import { useSiteReducedMotion, useScrollDirection } from "./motion-preferences";
import {
  ArrowUpRight,
  Check,
  ChevronRight,
  Code2,
  Database,
  Globe2,
  LayoutGrid,
  Layers3,
  MoreHorizontal,
  Bell,
  Plus,
  Search,
  SlidersHorizontal,
} from "lucide-react";

export function EnterpriseConcept() {
  const { ref, active } = useSceneActivity();
  const motionReduced = useSiteReducedMotion();
  const direction = useScrollDirection();
  const reduced = motionReduced || direction === "up";
  return (
    <div
      ref={ref}
      data-running={active}
      className="product-concept enterprise-concept"
      role="img"
      aria-label="Custom software interface concept with a connected platform overview, capacity chart, and deployment activity"
    >
      <div aria-hidden="true">
        <div className="concept-label">
          <span>01 / CUSTOM PLATFORMS</span>
          <span>INTERFACE CONCEPT</span>
        </div>
        <div className="platform-float">
          <div className="platform-preview">
            <div className="preview-toolbar">
              <span className="preview-brand">
                <span>Q</span>Workspace
              </span>
              <span className="preview-tools">
                <Search size={13} />
                <Bell size={13} />
                <span className="preview-avatar">QC</span>
              </span>
            </div>
            <div className="preview-workspace">
              <div className="preview-rail">
                <LayoutGrid size={16} />
                <Layers3 size={16} />
                <Database size={16} />
                <SlidersHorizontal size={16} />
              </div>
              <div className="preview-content">
                <div className="preview-heading">
                  <div>
                    <small>YOUR PLATFORM, CONNECTED.</small>
                    <strong>Built to scale.</strong>
                  </div>
                  <span className="preview-status">
                    <i />
                    Systems ready
                  </span>
                </div>
                <div className="preview-chart">
                  <div className="preview-chart-heading">
                    <span>Capacity overview</span>
                    <span>
                      Elastic infrastructure <ArrowUpRight size={12} />
                    </span>
                  </div>
                  <svg
                    viewBox="0 0 440 100"
                    preserveAspectRatio="none"
                    fill="none"
                  >
                    <defs>
                      <linearGradient
                        id="capacity-fill"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop stopColor="#fddf47" stopOpacity=".3" />
                        <stop offset="1" stopColor="#fddf47" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M0 25H440M0 55H440M0 85H440"
                      stroke="#c3b66e"
                      strokeOpacity=".1"
                    />
                    <path
                      d="M0 90C25 86 40 94 60 75S100 88 120 64S163 77 185 48S225 66 251 36S290 53 320 24S367 36 395 13S425 19 440 4V100H0Z"
                      fill="url(#capacity-fill)"
                    />
                    <motion.path
                      key={reduced ? "still-chart" : "animated-chart"}
                      initial={reduced ? false : { pathLength: 0 }}
                      whileInView={{ pathLength: 1 }}
                      viewport={{ once: true, amount: 0.5 }}
                      transition={{
                        duration: 1.8,
                        delay: 0.3,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      className="capacity-line"
                      pathLength="1"
                      d="M0 90C25 86 40 94 60 75S100 88 120 64S163 77 185 48S225 66 251 36S290 53 320 24S367 36 395 13S425 19 440 4"
                      stroke="#fddf47"
                      strokeWidth="2"
                      vectorEffect="non-scaling-stroke"
                    />
                    <path
                      className="capacity-sweep"
                      pathLength="1"
                      d="M0 90C25 86 40 94 60 75S100 88 120 64S163 77 185 48S225 66 251 36S290 53 320 24S367 36 395 13S425 19 440 4"
                      vectorEffect="non-scaling-stroke"
                    />
                  </svg>
                  <div className="preview-axis">
                    <span>BUILD</span>
                    <span>DEPLOY</span>
                    <span>SCALE</span>
                  </div>
                </div>
                <div className="preview-integrations">
                  <span>
                    <Code2 size={13} />
                    API gateway
                    <i />
                  </span>
                  <span>
                    <Database size={13} />
                    Data pipeline
                    <i />
                  </span>
                  <span>
                    <Globe2 size={13} />
                    Edge network
                    <i />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="concept-footnote">
          <span>One platform. Every possibility.</span>
          <span className="concept-coordinate">QC / 01</span>
        </div>
      </div>
    </div>
  );
}

export function MobileConcept() {
  const { ref, active } = useSceneActivity();
  return (
    <div
      ref={ref}
      data-running={active}
      className="product-concept mobile-concept"
      role="img"
      aria-label="Mobile interface concept with a clean project workspace and a synchronized update notification"
    >
      <div aria-hidden="true">
        <div className="concept-label">
          <span>02 / MOBILE</span>
          <span>INTERFACE CONCEPT</span>
        </div>
        <div className="mobile-composition">
          <div className="mobile-outline outline-back" />
          <div className="phone-float">
            <div className="app-preview">
              <div className="app-status">
                <span>9:41</span>
                <span>••• ▰</span>
              </div>
              <div className="app-header">
                <span className="app-monogram">Q</span>
                <MoreHorizontal size={19} />
              </div>
              <div className="app-greeting">
                <span>EVERYTHING, IN SYNC.</span>
                <strong>
                  Your
                  <br />
                  workspace.
                </strong>
              </div>
              <div className="app-feature">
                <div>
                  <span>YOUR NEXT RELEASE</span>
                  <ArrowUpRight size={16} />
                </div>
                <strong>
                  Ready when
                  <br />
                  you are.
                </strong>
                <div className="release-track">
                  <i />
                  <i />
                  <i />
                  <i />
                  <i />
                  <i />
                  <i />
                  <i />
                </div>
                <span className="app-feature-note">
                  <Check size={10} />
                  All checks complete
                </span>
              </div>
              <div className="app-projects">
                <span>
                  Projects
                  <Plus size={12} />
                </span>
                <div>
                  <span className="app-project-icon">
                    <Layers3 size={14} />
                  </span>
                  <span>
                    Business platform<small>In progress</small>
                  </span>
                  <ChevronRight size={12} />
                </div>
              </div>
              <div className="app-tabbar">
                <LayoutGrid size={15} />
                <Layers3 size={15} />
                <Bell size={15} />
              </div>
            </div>
          </div>
          <div className="toast-float">
            <div className="mobile-toast">
              <span>
                <Check size={15} />
              </span>
              <div>
                <strong>Seamlessly connected.</strong>
                <small>Across every device.</small>
              </div>
            </div>
          </div>
        </div>
        <div className="concept-footnote">
          <span>Considered down to the last touch.</span>
          <span className="concept-coordinate">iOS / ANDROID</span>
        </div>
      </div>
    </div>
  );
}
